import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquareText, Mail, Send } from "lucide-react";
import { BRAND, SERVICES } from "@/data/site";
import { Reveal, MaskedLine, ChapterLabel } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { icon: Phone, title: "Call Us", value: BRAND.phone, href: `tel:${BRAND.phoneRaw}`, testid: "contact-call-card" },
  { icon: MessageSquareText, title: "Text Us", value: BRAND.phone, href: `sms:${BRAND.phoneRaw}`, testid: "contact-text-card" },
  { icon: Mail, title: "Email Us", value: BRAND.email, href: `mailto:${BRAND.email}`, testid: "contact-email-card" },
];

const initialForm = { name: "", phone: "", email: "", service: SERVICES[0].name, details: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/estimates`, form);
      const saved = JSON.parse(localStorage.getItem("ysmp_estimates") || "[]");
      localStorage.setItem("ysmp_estimates", JSON.stringify([...saved, { ...form, at: new Date().toISOString() }]));
      toast.success("Estimate request sent! We'll get back to you fast.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong — please call or text us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-[#060f26] border border-cyan-400/20 rounded-xl px-5 py-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/70 focus:ring-1 focus:ring-cyan-400/40 transition-colors";

  return (
    <main data-testid="contact-page">
      <section className="relative pt-40 pb-24 bg-[#060f26] border-b border-cyan-400/10 grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskedLine delay={0.1}>
            <span className="font-mono-brand text-xs sm:text-sm tracking-[0.35em] uppercase text-cyan-400 font-bold">Free Estimates — No Pressure, Just Clean</span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-black italic uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl" data-testid="contact-title">
            <MaskedLine delay={0.25}>Ready For A</MaskedLine>
            <MaskedLine delay={0.4} className="text-cyan-400">Cleaner Tomorrow?</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-14">
          <div className="lg:col-span-2">
            <Reveal>
              <ChapterLabel number="01" text="Reach Out Directly" />
              <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-3xl sm:text-5xl">
                Talk To <span className="text-cyan-400 italic">The Owner</span>
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed">
                No call centers, no runaround. Call, text, or email and get a straight answer with a free, no-obligation estimate.
              </p>
            </Reveal>
            <div className="mt-10 flex flex-col gap-4">
              {CHANNELS.map(({ icon: Icon, title, value, href, testid }, i) => (
                <Reveal key={title} delay={i * 0.08}>
                  <a
                    href={href}
                    data-testid={testid}
                    className="group flex items-center gap-5 bg-[#0b1838] border border-cyan-400/15 rounded-2xl p-6 hover:border-cyan-400/50 hover:bg-cyan-400/5 transition-colors"
                  >
                    <span className="w-12 h-12 rounded-full bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center shrink-0 group-hover:bg-cyan-400 group-hover:text-[#030712] text-cyan-400 transition-colors">
                      <Icon className="w-5 h-5" />
                    </span>
                    <span>
                      <span className="block font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/70">{title}</span>
                      <span className="block font-display font-bold text-xl sm:text-2xl tracking-tight text-white mt-0.5">{value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} className="bg-[#0b1838] border border-cyan-400/20 rounded-3xl p-8 sm:p-12" data-testid="estimate-form">
              <ChapterLabel number="02" text="Request A Free Estimate" />
              <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-3xl sm:text-4xl mb-8">
                Tell Us About <span className="text-cyan-400 italic">Your Surface</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="estimate-name" className="block font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/80 mb-2">Name *</label>
                  <input id="estimate-name" data-testid="estimate-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="estimate-phone" className="block font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/80 mb-2">Phone *</label>
                  <input id="estimate-phone" data-testid="estimate-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(804) 555-0123" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="estimate-email" className="block font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/80 mb-2">Email</label>
                  <input id="estimate-email" data-testid="estimate-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="estimate-service" className="block font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/80 mb-2">Service *</label>
                  <select id="estimate-service" data-testid="estimate-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.name} className="bg-[#060f26]">{s.name}</option>
                    ))}
                    <option value="Other / Not Sure" className="bg-[#060f26]">Other / Not Sure</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="estimate-details" className="block font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/80 mb-2">Project Details</label>
                  <textarea id="estimate-details" data-testid="estimate-details-input" rows={4} value={form.details} onChange={set("details")} placeholder="Tell us what needs cleaning — driveway size, siding type, stains, etc." className={`${inputCls} resize-none`} />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="estimate-submit-button"
                className="mt-8 w-full sm:w-auto flex items-center justify-center gap-3 bg-cyan-400 text-[#030712] font-bold px-10 py-4 rounded-full hover:bg-cyan-300 hover:scale-[1.02] transition-transform disabled:opacity-60 disabled:hover:scale-100"
              >
                <Send className="w-4 h-4" />
                {sending ? "Sending..." : "Send My Free Estimate Request"}
              </button>
              <p className="mt-4 text-xs text-slate-500">We typically respond the same day. Prefer to talk? Call or text {BRAND.phone}.</p>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
